import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  PartnerLoginRequest, PartnerLoginResponse, PartnerMe, PartnerReseller,
} from '../types/partner';
import partnerApiService, { PARTNER_ME_KEY } from '../services/partnerApi';
import { onPasswordChangeRequired } from '../utils/portalAuthErrors';
import { loginWithGoogle as googleLoginFlow } from '../utils/portalGoogleLogin';

// Auth del PORTAL PARTNER — mismo patrón que CustomerAuthContext pero con
// storage propio (`partner_token` + `partner_me`): las tres sesiones (admin,
// cliente, partner) conviven sin pisarse. Tras el login se hace GET /partner/me
// y se cachea; refreshMe() lo reconsulta (p.ej. tras asignar créditos, para
// actualizar el saldo mayorista del hero).

interface PartnerAuthContextType {
  me: PartnerMe | null;
  reseller: PartnerReseller | null;
  isAuthenticated: boolean;
  // Contrato F1: entró con la contraseña de un solo uso y aún no ha creado la
  // suya. PartnerProtectedRoute pinta el gate bloqueante mientras sea true.
  mustChangePassword: boolean;
  login: (credentials: PartnerLoginRequest) => Promise<boolean>;
  // «Entrar con Google»: misma sesión que el login por contraseña.
  loginWithGoogle: () => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
  loading: boolean;
}

const PartnerAuthContext = createContext<PartnerAuthContextType | undefined>(undefined);

export const PartnerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [me, setMe] = useState<PartnerMe | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  // Rehidratación al montar: token → cache de /me → refresco en background.
  useEffect(() => {
    if (!partnerApiService.isAuthenticated()) {
      setLoading(false);
      return;
    }
    const storedMe = localStorage.getItem(PARTNER_ME_KEY);
    if (storedMe) {
      try {
        const cached: PartnerMe = JSON.parse(storedMe);
        setMe(cached);
        setMustChangePassword(!!cached.user?.must_change_password);
      } catch {
        localStorage.removeItem(PARTNER_ME_KEY);
      }
    }
    // Reconsulta tolerante: si el token caducó, el interceptor 401 limpia y
    // redirige; si falla por red mantenemos la cache y no rompemos.
    partnerApiService
      .getMe()
      .then((data) => {
        setMe(data);
        // /me es la fuente de verdad tras una recarga: el flag no vive en el
        // token, así que sin esto un F5 saltaría el gate.
        setMustChangePassword(!!data.user?.must_change_password);
        localStorage.setItem(PARTNER_ME_KEY, JSON.stringify(data));
      })
      .catch((err) => console.error('Partner /me refresh failed:', err))
      .finally(() => setLoading(false));
  }, []);

  // Red de seguridad del veto en SERVIDOR: un 403 PASSWORD_CHANGE_REQUIRED en
  // cualquier llamada levanta el gate sin cerrar sesión (el token es válido).
  // La cache de /me se marca también, para que un F5 no salte el gate.
  useEffect(
    () =>
      onPasswordChangeRequired('partner', () => {
        setMustChangePassword(true);
        setMe((prev) => {
          if (!prev) return prev;
          const updated: PartnerMe = {
            ...prev,
            user: { ...(prev.user || {}), must_change_password: true },
          };
          localStorage.setItem(PARTNER_ME_KEY, JSON.stringify(updated));
          return updated;
        });
      }),
    []
  );

  const login = async (credentials: PartnerLoginRequest): Promise<boolean> => {
    const auth = await partnerApiService.login(credentials);
    const data = await partnerApiService.getMe();
    setMe(data);
    // El login ya trae el flag; /me lo confirma (y es lo que sobrevive al F5).
    setMustChangePassword(
      !!(auth.must_change_password ?? data.user?.must_change_password)
    );
    localStorage.setItem(PARTNER_ME_KEY, JSON.stringify(data));
    return true;
  };

  /**
   * Login con Google del portal partner. El token que emite /google/complete es
   * el MISMO PartnerAuthResponse del login por contraseña, así que se guarda por
   * la vía de siempre (`partnerApiService.setAuthToken`) y a partir de ahí la
   * sesión es indistinguible: /me, gate de contraseña y refresco incluidos.
   */
  const loginWithGoogle = async (): Promise<boolean> => {
    const auth = await googleLoginFlow<PartnerLoginResponse>('partner');
    partnerApiService.setAuthToken(auth.access_token);
    const data = await partnerApiService.getMe();
    setMe(data);
    setMustChangePassword(
      !!(auth.must_change_password ?? data.user?.must_change_password)
    );
    localStorage.setItem(PARTNER_ME_KEY, JSON.stringify(data));
    return true;
  };

  /** Salida del gate obligatorio: POST /partner/auth/change-password. */
  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    await partnerApiService.changePassword({
      current_password: currentPassword,
      new_password: newPassword,
    });
    setMustChangePassword(false);
    setMe((prev) => {
      if (!prev) return prev;
      const updated: PartnerMe = {
        ...prev,
        user: { ...(prev.user || {}), must_change_password: false },
      };
      localStorage.setItem(PARTNER_ME_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const logout = () => {
    partnerApiService.clearAuth();
    setMe(null);
    setMustChangePassword(false);
  };

  const refreshMe = useCallback(async () => {
    try {
      const data = await partnerApiService.getMe();
      setMe(data);
      setMustChangePassword(!!data.user?.must_change_password);
      localStorage.setItem(PARTNER_ME_KEY, JSON.stringify(data));
    } catch (err) {
      console.error('Partner /me refresh failed:', err);
    }
  }, []);

  return (
    <PartnerAuthContext.Provider
      value={{
        me,
        reseller: me?.reseller ?? null,
        isAuthenticated: partnerApiService.isAuthenticated() && me !== null,
        mustChangePassword,
        login,
        loginWithGoogle,
        changePassword,
        logout,
        refreshMe,
        loading,
      }}
    >
      {children}
    </PartnerAuthContext.Provider>
  );
};

export const usePartnerAuth = () => {
  const context = useContext(PartnerAuthContext);
  if (context === undefined) {
    throw new Error('usePartnerAuth must be used within a PartnerAuthProvider');
  }
  return context;
};
